const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://cogneolink-api.craval.workers.dev';
const TOKEN_KEY = 'cogneolink-portal-token';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = tokenStore.get();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    tokenStore.clear();
    throw new Error('Unauthorized - please log in again');
  }
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface ScenarioOverview {
  id: string;
  name: string;
  scenarioType: string;
  status: string;
  lastRun: { status: string; startedAt: string; rowCount: number } | null;
  thisMonth: { total: number; success: number };
}

export interface ChangeRequest {
  id: string;
  scenario_id: string | null;
  request_type: string;
  title: string;
  description: string | null;
  status: string;
  created_at: number;
  resolved_at: number | null;
}

export interface MonthlyReport {
  id: string;
  year_month: string;
  total_jobs: number;
  success_jobs: number;
  failed_jobs: number;
  total_rows: number;
  sent_at: number | null;
}

export const api = {
  // Auth
  requestMagicLink: (email: string) =>
    request<{ ok: true; message: string }>('/portal/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyToken: (token: string) =>
    request<{
      token: string;
      user: { email: string; name: string | null; tenantId: string; role: string };
    }>(`/portal/auth/verify?token=${encodeURIComponent(token)}`),

  // Scenarios
  listScenarios: () => request<{ scenarios: ScenarioOverview[] }>('/portal/scenarios'),

  // Change requests
  listChangeRequests: () =>
    request<{ requests: ChangeRequest[] }>('/portal/change-requests'),

  createChangeRequest: (input: {
    scenarioId?: string;
    requestType: 'new_scenario' | 'modify' | 'pause' | 'delete' | 'question';
    title: string;
    description?: string;
  }) =>
    request<{ request: ChangeRequest }>('/portal/change-requests', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Reports
  listReports: () => request<{ reports: MonthlyReport[] }>('/portal/reports'),
};
